import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Building2, Copy, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransferPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    document.title = 'Bank Transfer - SizzleBox';
    const fetchData = async () => {
      try {
        const [orderRes, settingsRes] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`),
          axios.get(`${API}/settings`)
        ]);
        setOrder(orderRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      toast.success('Copied!');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500" />
      </div>
    );
  }

  if (!order || !settings) {
    return (
      <div className="min-h-screen bg-black pt-28 text-center">
        <p className="text-gray-400 text-xl">Order not found</p>
        <Link to="/"><Button className="mt-4 bg-fire-500 hover:bg-fire-600 rounded-full">Go Home</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="transfer-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center"><Logo className="h-12" /></Link>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6 max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-fire-500/20 rounded-full mb-4">
              <Building2 className="w-10 h-10 text-fire-500" />
            </div>
            <h1 className="font-heading text-4xl text-white mb-2">Bank Transfer</h1>
            <p className="text-gray-400">Complete your payment by transferring to our bank account</p>
          </div>

          {/* Order Reference */}
          <div className="bg-fire-500/10 border border-fire-500/30 rounded-xl p-4 mb-6 text-center">
            <p className="text-gray-400 text-sm">Order Reference</p>
            <p className="text-fire-500 font-mono font-bold text-xl" data-testid="transfer-order-number">#{order.order_number}</p>
          </div>

          {/* Amount to Pay */}
          <div className="bg-card rounded-2xl border border-white/10 p-6 mb-6 text-center">
            <p className="text-gray-400 text-sm mb-1">Amount to Pay</p>
            <p className="text-white font-bold text-4xl" data-testid="transfer-amount">₦{order.total.toLocaleString()}</p>
            {order.discount > 0 && (
              <p className="text-green-400 text-sm mt-1">Includes ₦{order.discount.toLocaleString()} discount</p>
            )}
          </div>

          {/* Bank Details */}
          <div className="bg-card rounded-2xl border border-white/10 overflow-hidden mb-6">
            <div className="px-6 py-4 bg-black/50 border-b border-white/10">
              <h2 className="text-white font-bold">Transfer Details</h2>
            </div>
            <div className="p-6 space-y-5">
              {/* Bank Name */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Bank Name</p>
                  <p className="text-white font-bold text-lg" data-testid="bank-name">{settings.bank_name || 'GTBank'}</p>
                </div>
              </div>

              {/* Account Number */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Account Number</p>
                  <p className="text-fire-500 font-mono font-bold text-2xl tracking-wider" data-testid="account-number">{settings.account_number || '0123456789'}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(settings.account_number || '0123456789', 'account')}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="copy-account-btn"
                >
                  {copied === 'account' ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-gray-400" />}
                </button>
              </div>

              {/* Account Name */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Account Name</p>
                  <p className="text-white font-semibold" data-testid="account-name">{settings.account_name || 'SizzleBox Ltd'}</p>
                </div>
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <div>
                  <p className="text-gray-400 text-sm">Amount</p>
                  <p className="text-fire-500 font-bold text-xl">₦{order.total.toLocaleString()}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(String(order.total), 'amount')}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="copy-amount-btn"
                >
                  {copied === 'amount' ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-card rounded-2xl border border-white/10 p-6 mb-8">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-fire-500" /> Instructions
            </h3>
            <ol className="space-y-2 text-gray-400 text-sm list-decimal list-inside">
              <li>Transfer the exact amount shown above to the bank account</li>
              <li>Use <span className="text-fire-500 font-mono">#{order.order_number}</span> as your payment reference</li>
              <li>Send proof of payment via WhatsApp to confirm your order</li>
              <li>Your order will be processed once payment is confirmed</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {settings.whatsapp && (
              <a
                href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, '').replace(/^0/, '234')}?text=Hi! I just made a bank transfer for Order %23${order.order_number} - ₦${order.total.toLocaleString()}. Please confirm.`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full py-4 font-bold text-lg" data-testid="transfer-whatsapp-btn">
                  Send Proof via WhatsApp
                </Button>
              </a>
            )}
            <Link to={`/order/${order.id}`} className="w-full">
              <Button className="w-full bg-fire-500 hover:bg-fire-600 text-white rounded-full py-4 font-bold" data-testid="transfer-track-btn">
                Track Order <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/" className="w-full">
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 rounded-full">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
